import React, { useState, useEffect } from "react";
import {
  Button,
  DatePicker,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  message,
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Tag,
  Select,
  Upload,
} from "antd";
import {
  PlusOutlined,
  DownloadOutlined,
  DeleteOutlined,
  SearchOutlined,
  DollarOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  ReloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import "./BanhBan.css";
import NavbarTrungThu from "../Layout/NavbarTrungThu"

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const BanhBan = () => {
  // States
  const [phieuBanHangList, setPhieuBanHangList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [form] = Form.useForm();
  const [stores, setStores] = useState([]);
  const [importedData, setImportedData] = useState([]); // New state for imported data
  const [quantities, setQuantities] = useState({}); // Store quantities per MaSP
  const [searchValue, setSearchValue] = useState(""); // New state for search
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // New state for selected rows
  const [selectedRows, setSelectedRows] = useState([]); // New state for selected rows data

  // Fixed store options for NguoiNhan
  const storeOptions = [
    { IDCH: 163, TenCuaHang: "CN.HCM" },
    { IDCH: 1663, TenCuaHang: "Bonpas" },
  ];

  // Initial fetch
  useEffect(() => {
    fetchStores();
    fetchPhieuBanHang();
  }, []);

  // Fetch stores for CuaHang dropdown
  const fetchStores = async () => {
    try {
      const response = await axios.get("https://doticom.vn:3000/stores");
      setStores(response.data);
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error("Lỗi khi tải danh sách cửa hàng");
    }
  };

  // Fetch order data
  const fetchPhieuBanHang = async (showMessage = false) => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://doticom.vn:3000/api/danhSachPhieuBanHangLoai2"
      );

      // Handle direct array response
      if (Array.isArray(response.data)) {
        setPhieuBanHangList(response.data);
        if (showMessage) {
          message.success("Cập nhật dữ liệu phiếu bán hàng thành công");
        }
      } else if (response.data.success) {
        // Handle wrapped response (if API sometimes returns this format)
        setPhieuBanHangList(response.data.data);
        if (showMessage) {
          message.success("Cập nhật dữ liệu phiếu bán hàng thành công");
        }
      } else {
        message.error(
          "Lỗi khi tải dữ liệu: " +
            (response.data.error || "Dữ liệu không hợp lệ")
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Lỗi khi tải dữ liệu: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Refresh handler
  const handleRefresh = () => {
    fetchPhieuBanHang(true);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setDatePickerOpen(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!phieuBanHangList || phieuBanHangList.length === 0) {
      message.warning("Không có dữ liệu để xuất");
      return;
    }

    const exportData = phieuBanHangList.map((item, index) => ({
      STT: index + 1,
      "ID Phiếu": item.IDPhieu,
      "Tên Người Tạo": item.TenKH,
      "Chi Nhánh": item.NguoiNhan,
      "Ngày Tạo": dayjs(item.NgayDat).format("DD/MM/YYYY"),
      "Chiết Khấu (%)": item.CK_PT,
      "Chiết Khấu TM": item.CK_TM,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Phiếu Bán Hàng");

    const fileName = `PhieuBanHang_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    message.success("Xuất Excel thành công");
  };

  // Show add order modal
  const showAddModal = () => {
    form.resetFields();
    form.setFieldsValue({
      TenKH: "",
      NguoiNhan: "",
      CK_PT: 0,
      CK_TM: 0,
    });
    setImportedData([]); // Reset imported data
    setQuantities({}); // Reset quantities
    setIsModalVisible(true);
  };

  // Handle modal cancel
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (importedData.length === 0) {
        message.error("Vui lòng import dữ liệu từ file Excel trước khi lưu");
        return;
      }

      const groupedPhieu = importedData.reduce((acc, item) => {
        const key = `${item.NgayNhan}_${item.MaBravo}`;  // Updated key to include MaBravo for separate grouping per MaBravo per date
        if (!acc[key]) {
          acc[key] = {
            TenKH: values.TenKH ? values.TenKH.trim() : "",
            NguoiNhan: values.NguoiNhan ? values.NguoiNhan.toString() : "",
            NgayNhan: item.NgayNhan,
            CK_PT: values.CK_PT || 0,
            CK_TM: values.CK_TM || 0,
            TenNguoiNhan: item.MaBravo, // Use MaBravo for TenNguoiNhan
            MaCH: item.MaCH,
            MaCH_CT: item.MaCH, // Assuming MaCH_CT is same as MaCH
            ChiTiet: [],
          };
        }
        acc[key].ChiTiet.push({
          MaSP: item.MaSP,
          SoLuong: item.SoLuong,
        });
        return acc;
      }, {});

      const phieuList = Object.values(groupedPhieu);

      setLoading(true);

      const response = await axios.post(
        "https://doticom.vn:3000/api/themPhieuBanHangBanhBan",
        phieuList
      );

      if (response.data.message) {
        message.success("Thêm phiếu bán hàng thành công");
        setIsModalVisible(false);
        fetchPhieuBanHang(false);
      } else {
        message.error(
          "Lỗi khi thêm phiếu bán hàng: " +
            (response.data.error || "Phản hồi không hợp lệ")
        );
      }
    } catch (error) {
      console.error("Submit error:", error);
      message.error(
        "Lỗi khi thêm phiếu bán hàng: " +
          (error.message || "Lỗi không xác định")
      );
    } finally {
      setLoading(false);
    }
  };
  // Delete order
  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Xác nhận xóa",
      content: "Bạn có chắc chắn muốn xóa phiếu bán hàng này?",
      okText: "Xóa",
      okType: "danger",
      cancelText: "Hủy",
      centered: true,
      width: 400,
      onOk: async () => {
        try {
          setLoading(true);
          const response = await axios.delete(
            `https://doticom.vn:3000/api/xoaPhieuBanHang/${id}`
          );

          if (response.data.message) {
            message.success("Xóa phiếu bán hàng thành công");
            fetchPhieuBanHang(false);
          } else if (response.data.success) {
            message.success("Xóa phiếu bán hàng thành công");
            fetchPhieuBanHang(false);
          } else {
            message.error(
              "Lỗi khi xóa: " + (response.data.error || "Phản hồi không hợp lệ")
            );
          }
        } catch (error) {
          console.error("Delete error:", error);
          message.error("Lỗi khi xóa: " + error.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Handle delete selected orders
  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) {
      message.warning("Vui lòng chọn ít nhất một phiếu để xóa");
      return;
    }

    Modal.confirm({
      title: "Xác nhận xóa",
      content: `Bạn có chắc chắn muốn xóa ${selectedRows.length} phiếu bán hàng đã chọn?`,
      okText: "Xóa",
      okType: "danger",
      cancelText: "Hủy",
      centered: true,
      width: 400,
      onOk: async () => {
        try {
          setLoading(true);
          const deletePromises = selectedRows.map((row) =>
            axios.delete(`https://doticom.vn:3000/api/xoaPhieuBanHang/${row.IDPhieu}`)
          );
          await Promise.all(deletePromises);
          message.success("Xóa phiếu bán hàng thành công");
          setSelectedRowKeys([]);
          setSelectedRows([]);
          fetchPhieuBanHang(false);
        } catch (error) {
          console.error("Delete selected error:", error);
          message.error("Lỗi khi xóa phiếu bán hàng: " + error.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    if (!phieuBanHangList || phieuBanHangList.length === 0) {
      return {
        totalOrders: 0,
        pendingOrders: 0,
        receivedOrders: 0,
        totalRevenue: 0,
      };
    }

    return phieuBanHangList.reduce(
      (summary, item) => {
        const orderTotal = item.ChiTiet
          ? item.ChiTiet.reduce(
              (sum, detail) => sum + (detail.ThanhTien || 0),
              0
            )
          : 0;
        const discountAmount =
          (orderTotal * (item.CK_PT || 0)) / 100 + (item.CK_TM || 0);
        const finalAmount = orderTotal - discountAmount;

        return {
          totalOrders: summary.totalOrders + 1,
          pendingOrders: summary.pendingOrders + (item.Status === 0 ? 1 : 0),
          receivedOrders: summary.receivedOrders + (item.Status === 1 ? 1 : 0),
          totalRevenue: summary.totalRevenue + finalAmount,
        };
      },
      {
        totalOrders: 0,
        pendingOrders: 0,
        receivedOrders: 0,
        totalRevenue: 0,
      }
    );
  };

  const summaryData = calculateSummary();

  // Calculate order total amount
  const calculateOrderTotal = (order) => {
    if (!order.ChiTiet || order.ChiTiet.length === 0) return 0;
    const subtotal = order.ChiTiet.reduce(
      (sum, detail) => sum + (detail.ThanhTien || 0),
      0
    );
    const discountAmount =
      (subtotal * (order.CK_PT || 0)) / 100 + (order.CK_TM || 0);
    return subtotal - discountAmount;
  };

  // Expandable row content
  const expandedRowRender = (record) => {
    if (!record.ChiTiet || record.ChiTiet.length === 0) {
      return <Text type="secondary">Không có chi tiết sản phẩm</Text>;
    }

    const detailColumns = [
      {
        title: "Mã SP",
        dataIndex: "MaSP",
        key: "MaSP",
        render: (text) => (text ? text.trim() : ""),
      },
      {
        title: "Tên Sản Phẩm",
        dataIndex: "TenSP",
        key: "TenSP",
      },
      {
        title: "Số Lượng",
        dataIndex: "SoLuong",
        key: "SoLuong",
        render: (text) => (text ? text.toString().trim() : "0"),
      },
      {
        title: "Đơn Giá",
        dataIndex: "DonGia",
        key: "DonGia",
        render: (value) =>
          value ? value.toLocaleString("vi-VN") + " đ" : "0 đ",
      },
      {
        title: "Thành Tiền",
        dataIndex: "ThanhTien",
        key: "ThanhTien",
        render: (value) =>
          value ? value.toLocaleString("vi-VN") + " đ" : "0 đ",
      },
    ];

    const subtotal = record.ChiTiet.reduce(
      (sum, detail) => sum + (detail.ThanhTien || 0),
      0
    );
    const discountAmount =
      (subtotal * (record.CK_PT || 0)) / 100 + (record.CK_TM || 0);
    const finalTotal = subtotal - discountAmount;

    return (
      <div style={{ padding: "16px", backgroundColor: "#fafafa" }}>
        <Table
          columns={detailColumns}
          dataSource={record.ChiTiet}
          pagination={false}
          rowKey="IDPhieu_CT"
          size="small"
        />
        <div style={{ marginTop: "16px", textAlign: "right" }}>
          <Space direction="vertical" size="small">
            <Text>
              Tạm tính: <strong>{subtotal.toLocaleString("vi-VN")} đ</strong>
            </Text>
            {(record.CK_PT > 0 || record.CK_TM > 0) && (
              <Text>
                Chiết khấu:{" "}
                <strong style={{ color: "#ff4d4f" }}>
                  -{discountAmount.toLocaleString("vi-VN")} đ
                </strong>
              </Text>
            )}
            <Text style={{ fontSize: "16px" }}>
              Tổng cộng:{" "}
              <strong style={{ color: "#1890ff" }}>
                {finalTotal.toLocaleString("vi-VN")} đ
              </strong>
            </Text>
          </Space>
        </div>
      </div>
    );
  };

  // Get store name by IDCH
  const getStoreNameByIDCH = (idch) => {
    if (!idch) return "Không xác định";

    const store = storeOptions.find(
      (store) => store.IDCH === parseInt(idch) || store.IDCH === idch
    );
    return store ? store.TenCuaHang : `${idch}`;
  };

  // Columns for order list table
  const columns = [
    {
      title: "STT",
      key: "STT",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Tên Người Tạo",
      dataIndex: "TenKH",
      key: "TenKH",
      sorter: (a, b) => a.TenKH.localeCompare(b.TenKH),
    },
    {
      title: "Chi Nhánh",
      dataIndex: "TenNguoiNhan",
      key: "TenNguoiNhan",
      sorter: (a, b) => getStoreNameByIDCH(a.TenNguoiNhan).localeCompare(getStoreNameByIDCH(b.TenNguoiNhan)),
      render: (idch) => getStoreNameByIDCH(idch),
    },
    {
      title: "Ngày Tạo",
      dataIndex: "NgayDat",
      key: "NgayDat",
      sorter: (a, b) => dayjs(a.NgayDat).isBefore(dayjs(b.NgayDat)) ? -1 : 1,
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Tổng Tiền",
      key: "TongTien",
      render: (_, record) => {
        const total = calculateOrderTotal(record);
        return (
          <Text strong style={{ color: total > 0 ? "#1890ff" : "#666" }}>
            {total.toLocaleString("vi-VN")} đ
          </Text>
        );
      },
    },
    {
      title: "CK (%)",
      dataIndex: "CK_PT",
      key: "CK_PT",
      width: 80,
    },
    {
      title: "CK TM",
      dataIndex: "CK_TM",
      key: "CK_TM",
      width: 100,
      render: (value) => (value ? value.toLocaleString("vi-VN") + " đ" : "0 đ"),
    },
    {
      title: "Thao Tác",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.IDPhieu)}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ];

  // Import Excel handler
  const handleImportExcel = async (file) => {
    // Ensure stores are loaded
    if (stores.length === 0) {
      try {
        await fetchStores();
      } catch (error) {
        message.error("Không thể tải danh sách cửa hàng. Vui lòng thử lại.");
        return false;
      }
    }

    setQuantities({}); // Reset quantities before importing

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const importedItems = [];
        let skippedRows = 0; // Đếm số hàng bị bỏ qua để debug
        let totalQuantity = 0; // Tính tổng số lượng (bao gồm âm và dương)
        let negativeQuantityCount = 0; // Đếm số hàng có SL âm (để log)

        const newQuantities = {}; // Temporary object to accumulate quantities

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];

          // Lấy dữ liệu với xử lý lỗi
          const rawNgayNhan = row[1] ? row[1].toString().trim() : "";
          const maSP = row[3] ? row[3].toString().trim() : "";
          const rawSoLuong = row[6] ? row[6].toString().trim() : "";

          // Phân tích ngày linh hoạt hơn (thử nhiều định dạng)
          let ngayNhan = null;
          if (rawNgayNhan) {
            const parsedDate = dayjs(
              rawNgayNhan,
              ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"],
              true
            ); // Thử nhiều định dạng
            if (parsedDate.isValid()) {
              ngayNhan = parsedDate.format("YYYY-MM-DD");
            }
          }

          // Phân tích số lượng (hỗ trợ số thập phân, mặc định 0 nếu lỗi)
          const soLuong = parseFloat(rawSoLuong) || 0;

          // Đếm SL âm để log (không chuyển thành dương)
          if (soLuong < 0) {
            negativeQuantityCount++;
          }

          // Lấy MaBravo và MaCH
          const maBravo = row[0] ? row[0].toString().trim() : "";
          const maCH =
            stores.find((store) => store.MaBravo === maBravo)?.IDCH || 0;

          // Kiểm tra điều kiện (bao gồm SL âm: chỉ cần maSP và soLuong !== 0)
          if (maSP && soLuong !== 0) {
            // Nếu thiếu ngayNhan, dùng ngày hiện tại làm mặc định
            if (!ngayNhan) {
              ngayNhan = dayjs().format("YYYY-MM-DD");
              console.warn(
                `Hàng ${i + 1}: Thiếu hoặc sai ngày, dùng mặc định ${ngayNhan}`
              );
            }

            importedItems.push({
              MaSP: maSP,
              SoLuong: soLuong, // Giữ nguyên SL âm (không chuyển dương)
              NgayNhan: ngayNhan,
              MaCH: maCH,
              MaBravo: maBravo, // Add MaBravo to imported data
            });
            totalQuantity += soLuong; // Cộng dồn tổng SL (bao gồm âm)
            newQuantities[maSP] = (newQuantities[maSP] || 0) + soLuong;
          } else {
            skippedRows++;
            console.warn(
              `Hàng ${
                i + 1
              } bị bỏ qua: MaSP=${maSP}, SoLuong=${soLuong}, NgayNhan=${rawNgayNhan}`
            );
          }
        }

        setQuantities(newQuantities); // Set the accumulated quantities

        if (importedItems.length > 0) {
          setImportedData(importedItems);
          message.success(
            `Đã import thành công ${importedItems.length} sản phẩm (tổng SL: ${totalQuantity}) từ Excel. Bỏ qua ${skippedRows} hàng không hợp lệ. Có ${negativeQuantityCount} sản phẩm với SL âm.`
          );
          console.log("Danh sách importedItems:", importedItems); // Debug chi tiết
        } else {
          message.warning("Không tìm thấy dữ liệu hợp lệ trong file Excel.");
        }
      } catch (error) {
        console.error("Import error:", error);
        message.error("Lỗi khi đọc file Excel: " + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  // Download Excel template
  const downloadExcelTemplate = () => {
    const templateData = [
      [
        "Ngày bán",
        "Tên Nhóm",
        "Mã hàng",
        "Tên hàng",
        "Giá bán",
        "S.L",
        "Thành tiền",
      ],
      [
        "03/09/2025",
        "BÁNH TRUNG THU",
        "BU002",
        "BÁNH NƯỚC GÀ QUAY JAMBON_1 Trứng_200gr",
        "99000",
        "1",
        "99000",
      ],
      [
        "10/09/2025",
        "BÁNH TRUNG THU",
        "BU002",
        "BÁNH NƯỚC GÀ QUAY JAMBON_1 Trứng_200gr",
        "99000",
        "2",
        "198000",
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    const fileName = `Template_dataIpos_${dayjs().format("YYYYMMDD")}.xlsx`;
    XLSX.writeFile(wb, fileName);
    message.success("Đã tải file mẫu thành công");
  };

  // Group imported data for display
  const groupImportedData = () => {
    if (!importedData || importedData.length === 0) return [];

    return importedData.reduce((acc, item) => {
      const key = `${item.NgayNhan}_${item.MaBravo}`;  // Updated key to include MaBravo for separate grouping per MaBravo per date
      if (!acc[key]) {
        acc[key] = {
          NgayNhan: item.NgayNhan,
          MaCH: item.MaCH,
          TenCuaHang: getStoreNameByIDCH(item.MaCH),
          MaBravo: item.MaBravo, // Add MaBravo for display
          ChiTiet: [],
        };
      }
      acc[key].ChiTiet.push({
        MaSP: item.MaSP,
        SoLuong: item.SoLuong,
      });
      return acc;
    }, {});
  };

  const groupedImportedData = Object.values(groupImportedData());

  // Columns for imported data table
  const importedColumns = [
    {
      title: "Ngày Bán",
      dataIndex: "NgayNhan",
      key: "NgayNhan",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Cửa Hàng",
      dataIndex: "MaBravo",
      key: "MaBravo",
    },
    {
      title: "Số Sản Phẩm",
      key: "SoSanPham",
      render: (_, record) => record.ChiTiet.length,
    },
  ];

  // Expandable row for imported data
  const importedExpandedRowRender = (record) => {
    const detailColumns = [
      {
        title: "Mã SP",
        dataIndex: "MaSP",
        key: "MaSP",
      },
      {
        title: "Số Lượng",
        dataIndex: "SoLuong",
        key: "SoLuong",
      },
    ];

    return (
      <Table
        columns={detailColumns}
        dataSource={record.ChiTiet}
        pagination={false}
        rowKey="MaSP"
        size="small"
      />
    );
  };

  // Group imported data into orders for display and submission
  const groupedPhieu = importedData.reduce((acc, item) => {
    const key = `${item.NgayNhan}_${item.MaBravo}`;  // Updated key to include MaBravo for separate grouping per MaBravo per date
    if (!acc[key]) {
      acc[key] = {
        TenKH: form.getFieldValue("TenKH")
          ? form.getFieldValue("TenKH").trim()
          : "",
        NguoiNhan: form.getFieldValue("NguoiNhan")
          ? form.getFieldValue("NguoiNhan").toString()
          : "",
        NgayNhan: item.NgayNhan,
        CK_PT: form.getFieldValue("CK_PT") || 0,
        CK_TM: form.getFieldValue("CK_TM") || 0,
        MaCH: item.MaCH,
        MaCH_CT: item.MaCH, // Assuming MaCH_CT is same as MaCH
        ChiTiet: [],
      };
    }
    acc[key].ChiTiet.push({
      MaSP: item.MaSP,
      SoLuong: item.SoLuong,
    });
    return acc;
  }, {});

  // Columns for grouped orders table
  const groupedColumns = [
    {
      title: "Ngày Nhận",
      dataIndex: "NgayNhan",
      key: "NgayNhan",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Cửa Hàng",
      dataIndex: "MaCH",
      key: "MaCH",
      render: (maCH) => getStoreNameByIDCH(maCH),
    },
    {
      title: "Số Sản Phẩm",
      key: "SoSanPham",
      render: (_, record) => record.ChiTiet.length,
    },
    {
      title: "Tên Người Tạo",
      dataIndex: "TenKH",
      key: "TenKH",
    },
    {
      title: "Nơi Nhận",
      dataIndex: "NguoiNhan",
      key: "NguoiNhan",
      render: (nguoiNhan) => getStoreNameByIDCH(nguoiNhan),
    },
    {
      title: "CK (%)",
      dataIndex: "CK_PT",
      key: "CK_PT",
    },
    {
      title: "CK TM",
      dataIndex: "CK_TM",
      key: "CK_TM",
      render: (value) => (value ? value.toLocaleString("vi-VN") + " đ" : "0 đ"),
    },
  ];

  // Expandable row for grouped orders
  const groupedExpandedRowRender = (record) => {
    const detailColumns = [
      {
        title: "Mã SP",
        dataIndex: "MaSP",
        key: "MaSP",
      },
      {
        title: "Số Lượng",
        dataIndex: "SoLuong",
        key: "SoLuong",
      },
    ];

    return (
      <Table
        columns={detailColumns}
        dataSource={record.ChiTiet}
        pagination={false}
        rowKey="MaSP"
        size="small"
      />
    );
  };

  // Filtered data based on search
  const filteredPhieuBanHangList = phieuBanHangList.filter((record) =>
    getStoreNameByIDCH(record.TenNguoiNhan)
      .toLowerCase()
      .includes(searchValue.toLowerCase())
  );

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  return (
    <>
    <NavbarTrungThu />
    <div className="banh-ban-container">
      <Card className="autumn-animation">
        <div className="header-container">
          <Title level={2}>
            <span>
              <ShoppingCartOutlined /> Quản Lý Phiếu Bán Hàng
            </span>
          </Title>

          <Space wrap>
            <Input.Search
              placeholder="Tìm kiếm theo chi nhánh"
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              title="Cập nhật dữ liệu"
            >
              Cập nhật
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportToExcel}>
              Xuất Excel
            </Button>
            {selectedRows.length > 0 && (
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteSelected}
              >
                Xóa Đã Chọn ({selectedRows.length})
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
              className="autumn-animation"
            >
              Thêm Phiếu Bán Hàng
            </Button>
          </Space>
        </div>

        {/* Summary Statistics 
        <div className="summary-section">
          <Row gutter={16}>
            <Col xs={24} sm={6} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Tổng Phiếu"
                  value={summaryData.totalOrders}
                  prefix={<ShoppingCartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Chờ Nhận"
                  value={summaryData.pendingOrders}
                  prefix={<CalendarOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <Card className="summary-card">
                <Statistic
                  title="Tổng Doanh Thu"
                  value={summaryData.totalRevenue}
                  prefix={<DollarOutlined />}
                  formatter={(value) => `${value.toLocaleString("vi-VN")} đ`}
                />
              </Card>
            </Col>
          </Row>
        </div>
*/}
        <Table
          columns={columns}
          dataSource={filteredPhieuBanHangList}
          rowKey="IDPhieu"
          loading={loading}
          rowSelection={rowSelection}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) =>
              record.ChiTiet && record.ChiTiet.length > 0,
          }}
          pagination={{
            pageSize: 10,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} phiếu bán hàng`,
          }}
          className="autumn-animation"
        />
      </Card>

      {/* Modal thêm phiếu bán hàng */}
      <Modal
        title={
          <span>
            <ShoppingCartOutlined /> Thêm Phiếu Bán Hàng
          </span>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        width={800}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            Lưu
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            CK_PT: 0,
            CK_TM: 0,
          }}
        >
          {/* Thông tin khách hàng */}
          <Card title="Thông Tin Khách Hàng" className="form-card">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="TenKH"
                  label="Tên Người Tạo"
                  rules={[
                    { required: true, message: "Vui lòng nhập tên người tạo" },
                  ]}
                >
                  <Input
                    placeholder="Nhập tên người tạo"
                    prefix={<UserOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="NguoiNhan"
                  label="Nơi Nhận"
                  rules={[
                    { required: true, message: "Vui lòng chọn nơi nhận" },
                  ]}
                >
                  <Select placeholder="Chọn nơi nhận">
                    {storeOptions.map((store) => (
                      <Select.Option key={store.IDCH} value={store.IDCH}>
                        {store.TenCuaHang}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Chi tiết sản phẩm */}
          <Card title="Chi Tiết Sản Phẩm" className="form-card">
            <div style={{ marginBottom: "16px" }}>
              <Space wrap>
                <Upload
                  accept=".xlsx,.xls"
                  beforeUpload={handleImportExcel}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />} type="default">
                    Import Excel
                  </Button>
                </Upload>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={downloadExcelTemplate}
                  type="dashed"
                >
                  Tải File Mẫu
                </Button>
              </Space>
              <div
                style={{
                  marginTop: "8px",
                  color: "#666",
                  fontSize: "12px",
                }}
              >
                Định dạng file Excel: Cột B (Ngày bán DD/MM/YYYY), Cột D (Mã
                hàng), Cột G (S.L)
              </div>
              {importedData.length > 0 && (
                <div style={{ marginTop: "16px" }}>
                  <Text strong>Đã import {Object.values(quantities).reduce((sum, qty) => sum + qty, 0)} sản phẩm</Text>
                </div>
              )}
            </div>
          </Card>

          {/* Hiển thị dữ liệu đã import */}
          {groupedImportedData.length > 0 && (
            <Card title="Dữ Liệu Đã Import" className="form-card">
              <Table
                columns={importedColumns}
                dataSource={groupedImportedData}
                rowKey={(record) => `${record.NgayNhan}_${record.MaCH}`}
                expandable={{
                  expandedRowRender: importedExpandedRowRender,
                  rowExpandable: (record) =>
                    record.ChiTiet && record.ChiTiet.length > 0,
                }}
                pagination={false}
                size="small"
              />
            </Card>
          )}

          {/* Chiết khấu */}
          <Card title="Chiết Khấu" className="form-card">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="CK_PT" label="Chiết Khấu (%)" initialValue={0}>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    placeholder="Nhập % chiết khấu"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="CK_TM"
                  label="Chiết Khấu Tiền Mặt"
                  initialValue={0}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                    placeholder="Nhập chiết khấu tiền mặt"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </Modal>
    </div>
    </>
  );
};

export default BanhBan;
